import React, { useState, useEffect, useCallback } from 'react';
import { Box, Flex, Text, Link, Spinner, Icon } from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { FiInfo, FiChevronLeft } from 'react-icons/fi';
import dayjs from 'dayjs';
import {
  CaseSummaryCard,
  CaseHistoryTimeline,
  CaseActionPanel,
  RefundDeadlineBanner,
} from 'components/refunds';
import { getRefundById, getUserDisplayName } from 'api/administrador/refunds';

function InfoBanner({ refund }) {
  const proceedEvent = (refund.events || [])
    .slice()
    .reverse()
    .find((e) => e.action === 'proceeded_refund');

  if (refund.status !== 'ready_for_payment' || !proceedEvent) {
    return null;
  }

  const userName = getUserDisplayName(proceedEvent.user);
  const dateStr = dayjs(proceedEvent.createdAt).format('DD-MM-YYYY · HH:mm');

  return (
    <Flex
      bg="#EBF8FF"
      border="1px solid #BEE3F8"
      borderRadius="md"
      p={4}
      mb={4}
      align="flex-start"
      gap={3}
    >
      <Icon as={FiInfo} color="#3182CE" mt={0.5} />
      <Text fontSize="sm" color="#2C5282">
        Este caso fue marcado por {userName} como no retenido el {dateStr}. Listo para procesar
        transferencia bancaria.
      </Text>
    </Flex>
  );
}

export default function RefundDetailAdmin() {
  const { id } = useParams();
  const [refund, setRefund] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadRefund = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getRefundById(id);
      setRefund(data);
    } catch (error) {
      console.error('Error cargando devolución:', error);
      setRefund(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadRefund();
  }, [loadRefund]);

  if (loading) {
    return (
      <Flex justify="center" py={20} px={4}>
        <Spinner color="#574feb" size="lg" />
      </Flex>
    );
  }

  if (!refund) {
    return (
      <Box py={10} px={4} textAlign="center">
        <Text color="red.500">Devolución no encontrada.</Text>
        <Link
          as={RouterLink}
          to="/plataforma/administrador/devoluciones"
          color="#574feb"
          mt={2}
          display="inline-flex"
          alignItems="center"
          gap={1}
          fontSize="sm"
        >
          <Icon as={FiChevronLeft} boxSize={4} />
          Regresar a devoluciones
        </Link>
      </Box>
    );
  }

  return (
    <Box px={{ base: 3, md: 6 }}>
      <Link
        as={RouterLink}
        to="/plataforma/administrador/devoluciones"
        color="#574feb"
        fontSize="sm"
        mb={4}
        display="inline-flex"
        alignItems="center"
        gap={1}
      >
        <Icon as={FiChevronLeft} boxSize={4} />
        Regresar a devoluciones
      </Link>

      <RefundDeadlineBanner refund={refund} />
      <InfoBanner refund={refund} />

      <Flex gap={6} align="flex-start" flexWrap="wrap">
        <Box flex="1" minW="300px">
          <CaseSummaryCard refund={refund} />
        </Box>
        <Box w={{ base: '100%', lg: '420px' }} flexShrink={0}>
          <CaseActionPanel refund={refund} onUpdated={loadRefund} />
          <CaseHistoryTimeline events={refund.events} status={refund.status} refund={refund} />
        </Box>
      </Flex>
    </Box>
  );
}
